import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import { DataGridPro, GridColDef, GridToolbarContainer, useGridApiContext } from "@mui/x-data-grid-pro";
import Chart, { useChart } from "src/components/chart";
import Iconify from "src/components/iconify";
import {
    CausalIncremental,
    CausalMetricKey,
    Experiment,
    ExperimentDailyRow,
    StrategyActivation,
    useGetExperimentCausalEffectQuery,
    useGetExperimentDailyQuery,
} from "src/context/api/bidStratTesting/api";
import StrategyParametersDialog from "./strategy-parameters-dialog";
import { tr } from "./i18n";
import CancelScheduleDialog from "./cancel-schedule-dialog";
import EndExperimentDialog from "./end-experiment-dialog";
import {
    BlackButton,
    COLORS,
    DangerButton,
    DashedArea,
    GreyButton,
    Icon,
    MetricSelect,
    PillSelect,
    SegmentedToggle,
    StateMessage,
} from "./components";
import {
    DEFAULT_COMPARISON_WEEKS,
    DisplayMode,
    GroupBy,
    METRICS,
    MIN_POST_DAYS,
    MetricKey,
    RECAP_METRIC_KEYS,
    STATISTICAL_METRIC_KEYS,
    Summary,
    unreliableStatistical,
    hasSignalActivation,
    causalStatus,
    difference,
    formatDay,
    formatDifference,
    formatMetric,
    formatRange,
    formatShortRange,
    groupCausalSeries,
    groupSeries,
    HIDDEN_RECAP_METRIC_KEYS,
    incrementalValue,
    isEffectConfirmed,
    normalize,
    strategyLabel,
    summarizePeriod,
    TRAFFIC_METRIC_KEYS,
} from "./utils";

const EMPTY_ROWS: ExperimentDailyRow[] = [];

// yyyy-mm-dd ↔ UTC midnight timestamp (the chart runs in UTC, no timezone shift)
const dayToTs = (day: string) => Date.parse(`${day}T00:00:00Z`);
const tsToDay = (timestamp: number) => new Date(timestamp).toISOString().slice(0, 10);
// "25 Mar"
const tsLabel = (timestamp: number) => formatDay(tsToDay(timestamp)).slice(0, 6);
const NoRowsOverlay = () => null;

// "Group by day / week" (Lily); the recap display mode keeps Total / Weekly / Daily
// Labels read in the current language (getters)
const GROUP_BY_OPTIONS: { value: GroupBy; readonly label: string }[] = [
    {
        value: "daily",
        get label() {
            return tr("selectors.day");
        },
    },
    {
        value: "weekly",
        get label() {
            return tr("selectors.week");
        },
    },
];
const MODE_OPTIONS: { value: DisplayMode; readonly label: string }[] = [
    {
        value: "daily",
        get label() {
            return tr("selectors.daily");
        },
    },
    {
        value: "weekly",
        get label() {
            return tr("selectors.weekly");
        },
    },
    {
        value: "total",
        get label() {
            return tr("selectors.total");
        },
    },
];

type GraphView = "descriptive" | "statistical";
const VIEW_OPTIONS: { value: GraphView; readonly label: string; readonly info: string }[] = [
    {
        value: "descriptive",
        get label() {
            return tr("analysis.descriptive");
        },
        get info() {
            return tr("analysis.descriptive_info");
        },
    },
    {
        value: "statistical",
        get label() {
            return tr("analysis.statistical");
        },
        get info() {
            return tr("analysis.statistical_info");
        },
    },
];

const isStatistical = (key: MetricKey): key is CausalMetricKey => (STATISTICAL_METRIC_KEYS as MetricKey[]).includes(key);

const GAP_COLORS: Record<number, string> = { 1: COLORS.positive, 0: COLORS.muted, [-1]: COLORS.negative };
// A rise of the Ad Cost or of the Blended CPC is bad news: red
const gapColor = (key: MetricKey, sign: number) => GAP_COLORS[METRICS[key].lowerIsBetter ? -sign : sign];

// Columns of the user feedback ticket, shown with the "Columns" button of the grid
const COLUMN_VISIBILITY = Object.fromEntries(HIDDEN_RECAP_METRIC_KEYS.map((key) => [key, false]));

const InfoTip = ({ title }: { title: string }) => (
    <Tooltip title={title} placement="top">
        <Box component="span" sx={{ display: "flex", flexShrink: 0 }}>
            <Icon name="info-circle" size={12} />
        </Box>
    </Tooltip>
);

const AXIS_LABEL_STYLE = { colors: "rgba(177, 185, 196, 0.9)", fontSize: "11px", fontWeight: 500 };

interface Props {
    experiment: Experiment;
    currency: string;
    locale: string;
}

interface RecapRow {
    id: "before" | "after" | "difference" | "incremental";
    label: string;
    date: string;
    strategyText: string;
    strategy: StrategyActivation[] | null;
    values: Record<MetricKey, string>;
    // Difference and Incremental rows: % shown next to each value, in its colour
    differences: Record<MetricKey, { text: string; color: string }> | null;
}

interface RecapToolbarProps {
    mode: DisplayMode;
    setMode: (mode: DisplayMode) => void;
    fileName: string;
}

function RecapToolbar({ mode, setMode, fileName }: RecapToolbarProps) {
    const apiRef = useGridApiContext();
    const iconButton = (title: string, icon: "columns" | "filter" | "export", onClick: () => void) => (
        <Tooltip title={title}>
            <IconButton aria-label={title} onClick={onClick} sx={{ width: 28, height: 28, p: 0.5, borderRadius: "8px" }}>
                {/* Export icon of the platform grids (Traffic page), bug report #19 */}
                {icon === "export" ? (
                    <Iconify icon="lets-icons:export" width={18} sx={{ color: COLORS.muted }} />
                ) : (
                    <Icon name={icon} size={18} />
                )}
            </IconButton>
        </Tooltip>
    );
    return (
        // The theme's toolbar padding has to be overridden on the right (as in the
        // Business Overview grid); 12px lines it up with the chart card above
        <GridToolbarContainer
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                pl: "12px",
                pr: "12px !important",
                py: 0,
                minHeight: 40,
                height: 40,
                borderBottom: "1px solid #eaeaea",
                bgcolor: "white",
            }}
        >
            <Stack direction="row" gap={1}>
                {iconButton(tr("recap.columns"), "columns", () => apiRef.current.showPreferences("columns" as any))}
                {iconButton(tr("recap.filters"), "filter", () => apiRef.current.showFilterPanel())}
                {iconButton(tr("recap.export"), "export", () => apiRef.current.exportDataAsCsv({ fileName }))}
            </Stack>
            <Stack direction="row" gap={1.5} alignItems="center">
                <PillSelect ariaLabel={tr("selectors.display")} value={mode} options={MODE_OPTIONS} onChange={setMode} />
            </Stack>
        </GridToolbarContainer>
    );
}

export default function ExperimentAnalysis({ experiment, currency, locale }: Props) {
    const { i18n } = useTranslation();
    // One read of the precomputed rows; every selector below works on them locally
    // Lot 3: a scheduled experiment only has its comparison period (before rows), computed
    // by the 13:00 job until its switch; no after period, no Causal Impact
    const scheduled = experiment.status === "scheduled";
    const [cancelOpen, setCancelOpen] = useState(false);
    const { data, isLoading, isFetching, isError, refetch } = useGetExperimentDailyQuery(experiment.id);
    const rows = data?.rows ?? EMPTY_ROWS;
    const loading = isLoading || (isFetching && !data);

    const [metric1, setMetric1] = useState<MetricKey>("brand_traffic");
    const [metric2, setMetric2] = useState<MetricKey>("ad_cost");
    // Eye of each metric selector: its series is hidden from the chart (one stays shown)
    const [hidden1, setHidden1] = useState(false);
    const [hidden2, setHidden2] = useState(false);
    const [groupBy, setGroupBy] = useState<GroupBy>("daily");
    const [mode, setMode] = useState<DisplayMode>("weekly");
    const [view, setView] = useState<GraphView>("descriptive");
    const statistical = view === "statistical";
    // Read once, the first time the Statistical graph is shown
    const causal = useGetExperimentCausalEffectQuery(experiment.id, { skip: !statistical || scheduled });
    const causalLoading = causal.isLoading || (causal.isFetching && !causal.data);
    const [parameters, setParameters] = useState<{ strategy: StrategyActivation[] | null } | null>(null);
    // Lot 1b.5: "End experiment" asks for a confirmation first (review ticket)
    const [endOpen, setEndOpen] = useState(false);

    // The Statistical graph has the 4 metrics of Causal Impact only (no Brand Searches)
    const handleView = (next: GraphView) => {
        if (next === "statistical") {
            const first = isStatistical(metric1) ? metric1 : STATISTICAL_METRIC_KEYS.find((key) => key !== metric2)!;
            const second = isStatistical(metric2) ? metric2 : STATISTICAL_METRIC_KEYS.find((key) => key !== first)!;
            setMetric1(first);
            setMetric2(second);
        }
        setView(next);
    };

    const points = useMemo(() => groupSeries(rows, groupBy), [rows, groupBy]);
    const afterDays = useMemo(() => rows.filter((row) => row.period === "after").length, [rows]);
    const causalRows = causal.data?.rows;
    const causalPoints = useMemo(
        () => (causalRows ? groupCausalSeries(causalRows, rows, groupBy) : []),
        [causalRows, rows, groupBy]
    );
    const incremental = statistical ? causal.data?.incremental ?? null : null;
    // Datetime axis: annotations are placed on exact timestamps, whatever the grouping
    const startTs = dayToTs(experiment.start_date);
    // Just the room of the "After ›" label right of the launch line (about 3% of the
    // width), so that it neither overlaps the axis nor disappears with no After day yet
    const axisMax = useMemo(() => {
        if (!points.length) return undefined;
        const firstTs = dayToTs(points[0].x);
        const lastTs = dayToTs(points[points.length - 1].x);
        return Math.max(lastTs, startTs + 0.03 * Math.max(startTs - firstTs, 0));
    }, [points, startTs]);

    const recapRows = useMemo<RecapRow[]>(() => {
        // Statistical graph: sum of (actual - predicted) over the after period. Brand
        // Searches is not modelled: "--".
        const incrementalRow = (summary: CausalIncremental): RecapRow => {
            const values = {} as Record<MetricKey, string>;
            const differences = {} as Record<MetricKey, { text: string; color: string }>;
            RECAP_METRIC_KEYS.forEach((key) => {
                if (!isStatistical(key)) {
                    values[key] = "--";
                    return;
                }
                const { value, pct } = incrementalValue(summary, key, mode);
                values[key] = formatDifference(value, METRICS[key].unit, false, currency, locale);
                // The CTR value is in pp, with its relative % next to it like the others (Lily).
                // An effect not confirmed (p-value of 0.05 or more, or none) is grey, whatever its sign
                differences[key] = {
                    text: formatDifference(pct, METRICS[key].unit, true, currency, locale),
                    color: isEffectConfirmed(summary, key) ? gapColor(key, Math.sign(pct ?? 0)) : COLORS.muted,
                };
            });
            return { id: "incremental", label: tr("recap.incremental"), date: "", strategyText: "", strategy: null, values, differences };
        };
        const before = normalize(summarizePeriod(rows, "before"), mode);
        const after = normalize(summarizePeriod(rows, "after"), mode);
        // A period without any day yet (first day of a running test) has no value, not 0
        const values = (summary: Summary) =>
            Object.fromEntries(
                RECAP_METRIC_KEYS.map((key) => [
                    key,
                    summary.days === 0 ? "--" : formatMetric(summary[key], METRICS[key].unit, currency, locale),
                ])
            ) as Record<MetricKey, string>;
        // Difference row (ticket 1a.6), designed as the Incremental one (Lily): the gap
        // After - Before in value, its % on the right; a CTR gap is in pp only (bug report #15)
        const differenceRow: RecapRow = {
            id: "difference",
            label: tr("recap.difference"),
            date: "",
            strategyText: "",
            strategy: null,
            values: Object.fromEntries(
                RECAP_METRIC_KEYS.map((key) => [
                    key,
                    after.days === 0
                        ? "--"
                        : formatDifference(difference(before[key], after[key], false), METRICS[key].unit, false, currency, locale),
                ])
            ) as Record<MetricKey, string>,
            differences: Object.fromEntries(
                RECAP_METRIC_KEYS.map((key) => {
                    const pct = after.days === 0 ? null : difference(before[key], after[key], true);
                    return [
                        key,
                        METRICS[key].unit === "percent" || pct === null
                            ? { text: "", color: COLORS.muted }
                            : { text: formatDifference(pct, METRICS[key].unit, true, currency, locale), color: gapColor(key, Math.sign(pct)) },
                    ];
                })
            ) as RecapRow["differences"],
        };
        const beforeRow: RecapRow = {
            id: "before",
            label: tr("recap.before"),
            date: formatShortRange(before.start, before.end),
            strategyText: strategyLabel(experiment.strategy_before),
            strategy: experiment.strategy_before,
            values: values(before),
            differences: null,
        };
        // Scheduled: the comparison period only
        if (experiment.status === "scheduled") return [beforeRow];
        return [
            beforeRow,
            {
                id: "after",
                label: tr("recap.after"),
                date: formatShortRange(after.start, after.end),
                strategyText: strategyLabel(experiment.strategy_after),
                strategy: experiment.strategy_after,
                values: values(after),
                differences: null,
            },
            differenceRow,
            ...(incremental ? [incrementalRow(incremental)] : []),
        ];
        // Labels in the new language when it changes: tr() reads it, the rule cannot see it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, mode, currency, locale, experiment, incremental, i18n.language]);

    const columns = useMemo<GridColDef<RecapRow>[]>(
        () => [
            // No title on screen (Figma), "Period" in the CSV export
            {
                field: "label",
                headerName: tr("recap.period"),
                renderHeader: () => null,
                width: 120,
                sortable: false,
                renderCell: ({ row, value }) => {
                    const info = row.id === "difference" || row.id === "incremental" ? tr(`recap.${row.id}_info`) : null;
                    return info ? (
                        <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
                            <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>
                                {value}
                            </Typography>
                            <InfoTip title={info} />
                        </Stack>
                    ) : (
                        value
                    );
                },
            },
            { field: "date", headerName: tr("recap.date"), width: 160, sortable: false },
            {
                field: "strategyText",
                // "Bidding strategy" (Lily), "Comparison strategy" in ticket 1a.6
                headerName: tr("recap.bidding_strategy"),
                flex: 1,
                // Fits the card at 1440px without horizontal scroll (bug report #13)
                minWidth: 140,
                sortable: false,
                renderCell: ({ value }) => (
                    <Tooltip title={value} placement="top-start">
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.text, cursor: value ? "help" : "default" }} noWrap>
                            {value}
                        </Typography>
                    </Tooltip>
                ),
            },
            ...RECAP_METRIC_KEYS.map((key): GridColDef<RecapRow> => {
                // Weekly and Daily volumes are means: "Avg. Ad Cost"; ratios are on the sums,
                // the same in the 3 modes. The CSV headers follow
                const headerName =
                    mode === "total" || METRICS[key].ratio ? METRICS[key].label : tr("recap.average", { label: METRICS[key].label });
                return {
                    field: key,
                    headerName,
                    flex: 1,
                    minWidth: 136,
                    sortable: false,
                    ...(TRAFFIC_METRIC_KEYS.includes(key) && {
                        renderHeader: () => (
                            <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
                                <span className="MuiDataGrid-columnHeaderTitle">{headerName}</span>
                                <InfoTip title={tr("recap.traffic_info")} />
                            </Stack>
                        ),
                    }),
                    valueGetter: (_value, row) => row.values[key],
                    renderCell: ({ row }) => {
                        const gap = row.differences?.[key];
                        // Figma: value on the left of the cell, evolution in a 64px+ slot on the right
                        return (
                            <Stack direction="row" alignItems="center" width={1} gap={1}>
                                <Typography
                                    noWrap
                                    sx={{
                                        flex: 1,
                                        // The full value, next to its change (bug report #14)
                                        flexShrink: 0,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: COLORS.text,
                                        fontVariantNumeric: "lining-nums tabular-nums",
                                    }}
                                >
                                    {row.values[key]}
                                </Typography>
                                <Typography
                                    noWrap
                                    sx={{
                                        flexShrink: 0,
                                        textAlign: "right",
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                        fontVariantNumeric: "lining-nums tabular-nums",
                                        color: gap ? gap.color : "inherit",
                                    }}
                                >
                                    {gap?.text}
                                </Typography>
                            </Stack>
                        );
                    },
                };
            }),
            {
                field: "parameters",
                headerName: tr("recap.parameters"),
                width: 110,
                sortable: false,
                filterable: false,
                disableExport: true,
                align: "right",
                headerAlign: "center",
                renderCell: ({ row }) =>
                    // No View when the strategy never ran with Signal: nothing to show (Lily)
                    row.id === "difference" || row.id === "incremental" || !hasSignalActivation(row.strategy) ? null : (
                    <Link
                        component="button"
                        underline="none"
                        onClick={() => setParameters({ strategy: row.strategy })}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, fontWeight: 500, color: COLORS.link }}
                    >
                        {tr("recap.view")}
                        <Icon name="arrow-up-right" size={16} />
                    </Link>
                    ),
            },
        ],
        // Headers in the new language when it changes, "Avg." in Weekly and Daily
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n.language, mode]
    );

    // Statistical graph, per metric: the interval band, the prediction (dashed)
    // and the actual value (plain); the band and the prediction from the start date
    // Shown metrics; Metric 2 keeps its right axis when Metric 1 is hidden
    const shownKeys = [hidden1 ? null : metric1, hidden2 ? null : metric2].filter((key): key is MetricKey => key !== null);
    const statisticalKeys = shownKeys.filter(isStatistical);
    const seriesNames = (key: MetricKey) =>
        statistical ? [`${METRICS[key].label} interval`, `${METRICS[key].label} predicted`, METRICS[key].label] : METRICS[key].label;
    const causalByDay = useMemo(() => new Map(causalPoints.map((point) => [point.x, point])), [causalPoints]);
    const pointsByDay = useMemo(() => new Map(points.map((point) => [point.x, point])), [points]);

    const tooltipLine = (color: string, label: string, value: string, dashed = false) => `
        <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
            <span style="display:inline-block;width:2px;height:12px;border-radius:2px;${
                dashed ? `border-left:2px dashed ${color};` : `background-color:${color};`
            }flex-shrink:0;"></span>
            <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                <span>${label}</span>
                <span>${value}</span>
            </div>
        </div>`;

    const tooltipLines = (day: string) => {
        if (statistical) {
            const point = causalByDay.get(day);
            if (!point) return "";
            return statisticalKeys
                .map((key) => {
                    const { actual, predicted, lower_bound, upper_bound } = point.values[key];
                    const format = (value: number | null) => formatMetric(value, METRICS[key].unit, currency, locale);
                    const line = tooltipLine(METRICS[key].color, METRICS[key].label, format(actual));
                    if (predicted === null) return line;
                    return (
                        line +
                        tooltipLine(METRICS[key].color, tr("analysis.predicted"), `${format(predicted)} (${format(lower_bound)} – ${format(upper_bound)})`, true)
                    );
                })
                .join("");
        }
        const point = pointsByDay.get(day);
        if (!point) return "";
        return shownKeys
            .map((key) => tooltipLine(METRICS[key].color, METRICS[key].label, formatMetric(point[key], METRICS[key].unit, currency, locale)))
            .join("");
    };

    const statisticalAxis = (key: MetricKey) => {
        if (!isStatistical(key)) return {};
        const values = causalPoints
            .flatMap((point) => [point.values[key].actual, point.values[key].predicted])
            .filter((value): value is number => value !== null && Number.isFinite(value));
        if (!values.length) return {};
        const low = Math.min(...values);
        const high = Math.max(...values);
        const margin = (high - low) * 0.1 || Math.abs(high) * 0.1 || 1;
        return { min: low >= 0 ? Math.max(0, low - margin) : low - margin, max: high + margin };
    };

    const chartOptions = useChart({
        chart: { animations: { enabled: false }, zoom: { enabled: false }, toolbar: { show: false } },
        colors: statistical
            ? statisticalKeys.flatMap((key) => [METRICS[key].color, METRICS[key].color, METRICS[key].color])
            : shownKeys.map((key) => METRICS[key].color),
        stroke: statistical
            ? { width: statisticalKeys.flatMap(() => [0, 2, 2]), dashArray: statisticalKeys.flatMap(() => [0, 5, 0]), curve: "smooth" }
            : { width: 2, curve: "smooth" },
        fill: statistical ? { type: "solid", opacity: statisticalKeys.flatMap(() => [0.16, 1, 1]) } : undefined,
        legend: { show: false },
        grid: { borderColor: "rgba(145, 158, 171, 0.2)", strokeDashArray: 3 },
        xaxis: {
            type: "datetime",
            max: axisMax,
            labels: {
                style: AXIS_LABEL_STYLE,
                datetimeUTC: true,
                formatter: (_value: string, timestamp?: number) => (timestamp ? tsLabel(timestamp) : ""),
            },
            axisBorder: { show: false },
            tooltip: { enabled: false },
        },
        yaxis: shownKeys.map((key) => ({
            seriesName: seriesNames(key),
            opposite: key === metric2,
            // Statistical graph: the axis follows the actual and predicted values, not the
            // interval, whose bounds can be huge (Blended CPC on a low traffic): the band
            // is cut at the edge of the chart and the evolution stays readable (Lily)
            ...(statistical ? statisticalAxis(key) : {}),
            tickAmount: 4,
            labels: {
                style: { colors: [AXIS_LABEL_STYLE.colors], fontSize: "11px", fontWeight: 500 },
                formatter: (value: number) => formatMetric(value, METRICS[key].unit, currency, locale),
            },
        })),
        // Same tooltip as the Business Overview and Explore charts (.cb-clean-tooltip)
        tooltip: {
            shared: true,
            intersect: false,
            // Series do not all have the same days (band and prediction start at the
            // start date): the hovered point is found by its date
            custom: ({ seriesIndex, dataPointIndex, w }: { seriesIndex: number; dataPointIndex: number; w: any }) => {
                const timestamp = w?.globals?.seriesX?.[seriesIndex]?.[dataPointIndex];
                if (timestamp === undefined) return "";
                const day = tsToDay(timestamp);
                const lines = tooltipLines(day);
                if (!lines) return "";
                const date = groupBy === "weekly" ? tr("analysis.week_of", { date: formatDay(day) }) : formatDay(day);
                return `
                    <div class="cb-clean-tooltip" style="background:#fcfcfd;border:1px solid rgba(42,50,59,0.2);border-radius:12px;box-shadow:1px 2px 10px 0 rgba(0,0,0,0.06);overflow:hidden;min-width:240px;">
                        <div style="padding:12px 12px 0 12px;">
                            <p style="font-family:Public Sans,sans-serif;font-size:14px;font-weight:700;color:#333c47;white-space:nowrap;margin:0;">${date}</p>
                        </div>
                        <div style="padding:12px;display:flex;flex-direction:column;gap:6px;">${lines}</div>
                    </div>`;
            },
        },
        annotations: {
            xaxis: [
                {
                    x: startTs,
                    borderColor: COLORS.muted,
                    strokeDashArray: 4,
                    label: {
                        text: tr("analysis.chart_before"),
                        textAnchor: "end",
                        orientation: "horizontal",
                        offsetX: -8,
                        borderWidth: 0,
                        style: { background: "transparent", color: "#016AA4", fontSize: "11px", fontWeight: 500 },
                    },
                },
                {
                    x: startTs,
                    borderColor: "transparent",
                    label: {
                        text: tr("analysis.chart_after"),
                        textAnchor: "start",
                        orientation: "horizontal",
                        offsetX: 8,
                        borderWidth: 0,
                        style: { background: "transparent", color: COLORS.secondary, fontSize: "11px", fontWeight: 500 },
                    },
                },
            ],
        },
    });

    const series = statistical
        ? statisticalKeys.flatMap((key) => {
              const predicted = causalPoints.filter((point) => point.values[key].predicted !== null);
              return [
                  {
                      name: `${METRICS[key].label} interval`,
                      type: "rangeArea",
                      data: predicted
                          .filter(({ values }) => values[key].lower_bound !== null && values[key].upper_bound !== null)
                          .map((point) => ({ x: dayToTs(point.x), y: [point.values[key].lower_bound, point.values[key].upper_bound] })),
                  },
                  {
                      name: `${METRICS[key].label} predicted`,
                      type: "line",
                      data: predicted.map((point) => ({ x: dayToTs(point.x), y: point.values[key].predicted })),
                  },
                  {
                      name: METRICS[key].label,
                      type: "line",
                      data: causalPoints.map((point) => ({ x: dayToTs(point.x), y: point.values[key].actual })),
                  },
              ];
          })
        : shownKeys.map((key) => ({
              name: METRICS[key].label,
              data: points.map((point) => ({ x: dayToTs(point.x), y: point[key] })),
          }));

    // Statistical graph: error of a metric shown instead of the graph
    const statusErrors = causal.data
        ? statisticalKeys.map((key) => causalStatus(causal.data!.status, key)).filter((error): error is string => !!error)
        : [];

    const renderChartArea = () => {
        // Scheduled without its comparison period yet (before the next 13:00 job): the message
        if (scheduled && (loading || isError || rows.length === 0)) {
            return (
                <DashedArea>
                    <StateMessage
                        title={tr("analysis.scheduled_title")}
                        description={
                            <>
                                {tr("analysis.scheduled_start")}{" "}
                                <Box component="span" sx={{ fontWeight: 600, color: COLORS.text }}>
                                    {formatDay(experiment.start_date)}
                                </Box>
                            </>
                        }
                    />
                </DashedArea>
            );
        }
        if (loading || (statistical && causalLoading)) {
            return (
                <DashedArea>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Icon name="loading-02" size={20} />
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: COLORS.title }}>
                            {tr("analysis.loading")}
                        </Typography>
                    </Stack>
                </DashedArea>
            );
        }
        if (isError || (statistical && causal.isError)) {
            return (
                <DashedArea>
                    <StateMessage title={tr("analysis.load_error")} description={tr("common.try_again")} />
                    <GreyButton onClick={() => (isError ? refetch() : causal.refetch())} sx={{ mt: 1.5 }}>
                        {tr("common.retry")}
                    </GreyButton>
                </DashedArea>
            );
        }
        if (rows.length === 0) {
            return (
                <DashedArea>
                    <StateMessage
                        title={tr("analysis.insufficient")}
                        description={tr("analysis.insufficient_daily")}
                    />
                </DashedArea>
            );
        }
        if (statistical) {
            if (afterDays < MIN_POST_DAYS) {
                return (
                    <DashedArea>
                        <StateMessage title={tr("analysis.insufficient")} description={tr("analysis.insufficient_days")} />
                    </DashedArea>
                );
            }
            // An error first: a metric in error may have no row in the view
            if (statusErrors.length) {
                return (
                    <DashedArea>
                        <StateMessage title={tr("analysis.statistical_error")} description={statusErrors.join(" · ")} />
                    </DashedArea>
                );
            }
            if (!causal.data?.rows.length) {
                // A completed experiment computed since its end is frozen: no later run will add it
                const frozen =
                    experiment.status === "completed" &&
                    !!experiment.end_date &&
                    !!data?.snapshot_date &&
                    data.snapshot_date > experiment.end_date;
                return (
                    <DashedArea>
                        <StateMessage
                            title={tr("analysis.insufficient")}
                            description={
                                frozen
                                    ? tr("analysis.frozen")
                                    : tr("analysis.statistical_daily")
                            }
                        />
                    </DashedArea>
                );
            }
            return <Chart key="statistical" type="rangeArea" series={series} options={chartOptions} height={260} />;
        }
        return <Chart key="descriptive" type="line" series={series} options={chartOptions} height={260} />;
    };

    // "Insufficient data" of the statistical graph: the grid keeps its headers only (Figma)
    const statisticalInsufficient =
        statistical && (afterDays < MIN_POST_DAYS || (!causalLoading && !causal.isError && !statusErrors.length && !causal.data?.rows.length));
    const showRecap = !loading && !isError && rows.length > 0 && !statisticalInsufficient;

    return (
        <Stack gap={1.5} sx={{ pl: "38px", pr: 2, py: 2, bgcolor: "white" }} data-testid={`experiment-analysis-${experiment.id}`}>
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Stack gap={0.25} flex={1}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, color: COLORS.title }}>
                        {experiment.name}
                    </Typography>
                    <Stack direction="row" gap={1} sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.2, color: COLORS.secondary }}>
                        <span>{experiment.campaign_name}</span>
                        <span>{formatRange(experiment.start_date, experiment.end_date)}</span>
                    </Stack>
                </Stack>
                {/* No Causal Impact before the switch: the descriptive graph only */}
                {!scheduled && (
                    <SegmentedToggle ariaLabel={tr("analysis.graph")} value={view} options={VIEW_OPTIONS} onChange={handleView} />
                )}
                {/* No "Data snapshot" label next to the graph switcher (Lily's bug report #8, Figma) */}
                {experiment.status === "running" && (
                    <BlackButton onClick={() => setEndOpen(true)}>
                        {tr("analysis.end")}
                    </BlackButton>
                )}
                {scheduled && <DangerButton onClick={() => setCancelOpen(true)}>{tr("table.cancel_schedule")}</DangerButton>}
            </Stack>
            <CancelScheduleDialog experiment={cancelOpen ? experiment : null} onClose={() => setCancelOpen(false)} />
            <EndExperimentDialog experiment={endOpen ? experiment : null} onClose={() => setEndOpen(false)} />

            <Stack gap={1.5} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "8px", p: 1.5, bgcolor: "white" }}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, color: COLORS.text, flex: 1 }}>
                        {tr("analysis.metrics_evolution")}
                    </Typography>
                    <MetricSelect
                        label={tr("selectors.metric_1")}
                        value={metric1}
                        onChange={setMetric1}
                        excluded={metric2}
                        options={statistical ? STATISTICAL_METRIC_KEYS : undefined}
                        hidden={hidden1}
                        onToggleHidden={() => setHidden1((current) => !current)}
                        cannotHide={!hidden1 && hidden2}
                    />
                    <MetricSelect
                        label={tr("selectors.metric_2")}
                        value={metric2}
                        onChange={setMetric2}
                        excluded={metric1}
                        options={statistical ? STATISTICAL_METRIC_KEYS : undefined}
                        hidden={hidden2}
                        onToggleHidden={() => setHidden2((current) => !current)}
                        cannotHide={!hidden2 && hidden1}
                    />
                    <PillSelect
                        ariaLabel={tr("selectors.group_by")}
                        label={tr("selectors.group_by")}
                        value={groupBy}
                        options={GROUP_BY_OPTIONS}
                        onChange={setGroupBy}
                    />
                </Stack>
                {statistical && experiment.comparison_weeks < DEFAULT_COMPARISON_WEEKS && (
                    <Alert severity="warning" sx={{ py: 0, fontSize: 13, fontWeight: 500 }}>
                        {unreliableStatistical()}
                    </Alert>
                )}
                {scheduled && rows.length > 0 && (
                    <Alert severity="info" sx={{ py: 0, fontSize: 13, fontWeight: 500 }}>
                        {tr("analysis.scheduled_title")} · {tr("analysis.scheduled_start")} {formatDay(experiment.start_date)}
                    </Alert>
                )}
                <Box height={260}>{renderChartArea()}</Box>
            </Stack>

            <Box
                sx={{
                    border: "1px solid #EAEAEA",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0px 6px 12px 0px rgba(145, 158, 171, 0.06)",
                }}
            >
                <DataGridPro
                    autoHeight
                    rows={showRecap ? recapRows : []}
                    columns={columns}
                    rowHeight={44}
                    columnHeaderHeight={36}
                    hideFooter
                    disableColumnMenu
                    disableRowSelectionOnClick
                    initialState={{ columns: { columnVisibilityModel: COLUMN_VISIBILITY } }}
                    slots={{
                        // Loading / no result: headers only, no partial data
                        noRowsOverlay: NoRowsOverlay,
                        toolbar: RecapToolbar as any,
                    }}
                    slotProps={{
                        toolbar: { mode, setMode, fileName: `experiment_${experiment.id}_${mode}` } as any,
                    }}
                    sx={{
                        border: "none",
                        borderRadius: 0,
                        minHeight: 80,
                        "--DataGrid-rowBorderColor": COLORS.border,
                        "& .MuiDataGrid-columnHeader": { bgcolor: "#FCFCFD", borderRight: `1px solid ${COLORS.border}`, px: 1.5 },
                        // One line on hover too (sort icon shown): the title is cut with an ellipsis.
                        // !important beats data-grid-custom.css, which wraps every header title of the app
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: 14,
                            fontWeight: 600,
                            color: COLORS.secondary,
                            whiteSpace: "nowrap !important",
                            overflow: "hidden !important",
                            textOverflow: "ellipsis",
                            lineHeight: "normal !important",
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent": { overflow: "hidden" },
                        // Columns can be resized (as on the other grids): the handle stays invisible,
                        // the header border is the separator of the design, and shows on hover
                        "& .MuiDataGrid-columnSeparator": { color: "transparent" },
                        "& .MuiDataGrid-columnSeparator--resizable:hover": { color: COLORS.muted },
                        "& .MuiDataGrid-cell": {
                            fontSize: 14,
                            fontWeight: 500,
                            color: COLORS.text,
                            px: 1.5,
                            borderRight: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                        },
                    }}
                />
            </Box>
            <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{tr("recap.scope_note")}</Typography>

            <StrategyParametersDialog
                open={parameters !== null}
                strategy={parameters?.strategy ?? null}
                campaignName={experiment.campaign_name}
                onClose={() => setParameters(null)}
            />
        </Stack>
    );
}
